import { useEffect, useState } from "react";
import { ShoppingBasket, Utensils, Shirt, Laptop, PawPrint, Plane, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const KEY = "first_visit_dismissed_v1";

// Six punchy first-visit tiles that cover 80% of "why did you come here?" intents.
// Each maps to an existing category name (matches CouponCard categories).
const TILES = [
  { label: "Groceries",  category: "Grocery",     Icon: ShoppingBasket, bg: "#4ecdc4", tag: "Weekly ads + digital coupons" },
  { label: "Food & Drink", category: "Food",      Icon: Utensils,        bg: "#ff8b6b", tag: "Restaurants, delivery, coffee" },
  { label: "Clothing",   category: "Fashion",     Icon: Shirt,           bg: "#f7c873", tag: "Adidas, Nike, Old Navy…" },
  { label: "Tech",       category: "Electronics", Icon: Laptop,          bg: "#88bfff", tag: "Laptops, TVs, gaming" },
  { label: "Pet Stuff",  category: "Pets",        Icon: PawPrint,        bg: "#c4a3f5", tag: "PetSmart, Chewy, food & meds" },
  { label: "Travel",     category: "Travel",      Icon: Plane,           bg: "#7dbb6e", tag: "Flights, hotels, rideshare" },
];

/**
 * FirstVisitPicker — a bold "What are you shopping for today?" tile grid.
 *
 * Renders ONLY for users who haven't dismissed it yet. On tile-click:
 *  1) sets the parent's category filter via callback
 *  2) smooth-scrolls down to the feed
 *  3) marks itself dismissed so return visits get the full feed
 * A small "Skip →" link is available for people who just want the raw feed.
 */
export default function FirstVisitPicker({ onPick }) {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(KEY) === "1";
      // Signed-in users are past onboarding, no need to re-guide them.
      setHidden(dismissed || !!user);
    } catch {
      setHidden(false);
    }
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    setHidden(true);
  };

  const pick = (cat) => {
    if (typeof onPick === "function") onPick(cat);
    dismiss();
    // Give React one tick to apply the filter, then scroll to feed.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="feed-grid"]') ||
                 document.querySelector('[data-testid="category-chips"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  if (hidden) return null;

  return (
    <section
      data-testid="first-visit-picker"
      className="mb-10 border-2 border-[#2d2a32] bg-[#faf5e6] rounded-2xl p-6 md:p-8 hard-shadow-sm relative"
    >
      <button
        onClick={dismiss}
        data-testid="first-visit-skip"
        className="absolute top-4 right-5 text-[10px] tracking-[0.2em] font-semibold text-[#5c5c5c] hover:text-[#2d2a32]"
      >
        SKIP →
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-[#ff6b6b]" />
        <span className="eyebrow text-[10px] text-[#5c5c5c]">START HERE · NEW VISITOR</span>
      </div>
      <h2 className="serif text-3xl md:text-4xl leading-tight tracking-tight mb-2">
        What are you shopping for today?
      </h2>
      <p className="text-sm text-[#3a3a3a] mb-6 max-w-xl">
        Tap a tile — we'll show you the 5-10 verified codes clippers are actually using this week.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {TILES.map(({ label, category, Icon, bg, tag }) => (
          <button
            key={label}
            onClick={() => pick(category)}
            data-testid={`first-visit-tile-${category.toLowerCase()}`}
            className="group text-left border-2 border-[#2d2a32] rounded-xl p-4 bg-white hover:-translate-y-1 hover:shadow-[6px_6px_0px_#2d2a32] transition-transform flex items-start gap-3"
          >
            <div
              className="shrink-0 w-11 h-11 rounded-lg border-2 border-[#2d2a32] flex items-center justify-center"
              style={{ background: bg }}
            >
              <Icon className="w-5 h-5 text-[#2d2a32]" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <div className="serif text-lg leading-tight font-semibold text-[#2d2a32]">{label}</div>
              <div className="text-[11px] text-[#5c5c5c] mt-0.5 leading-snug truncate">{tag}</div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
